import { defineStore } from 'pinia';
import * as mobileApp from '../globals/utilities/MobileApp';

import ServiceMessage from '../types/interfaces/global/ServiceMessage';
import serverFetch from '../globals/ServerFetch';
import { HelloResponse } from '../globals/ServerManager';
import { useAppStore } from './app';

// eslint-disable-next-line import/prefer-default-export
export const useImportantMessageStore = defineStore({
	id: 'importantMessage',
	state: () => ({
		/**
		 * Obiekt zawierający ważny komunikat.
		 */
		message: undefined as ServiceMessage | undefined,
	}),
	actions: {
		/**
		 * Przetwarza ważny komunikat, np. o braku środków na koncie z odpowiedzi na `hello.ofer`.
		 * Jeżeli nie ma żadnych innych, sprawdź czy można wyświetlić komunikat o powiadomieniach Push
		 * @param rsvp Odpowiedź serwera
		 * @returns Obiekt komunikatu
		 */
		update(rsvp: HelloResponse) {
			const skipCheck = false;
			const displayServiceMessages = rsvp.canShowImportantMessage;

			// Nie pokazuj żadnej wiadomości w tej sesji
			if (!displayServiceMessages && !skipCheck) {
				return;
			}

			// W innym przypadku...
			const msg = rsvp.importantMessage;

			if (msg) {
				this.message = {
					id: msg.id,
					type: msg.type || 'info',
					title: msg.title,
					message: msg.message.replace(/<br>/g, '').replace(/<br\/>/g, ''),
					button: msg.buttonLabel || 'Dowiedz się więcej',
					dismissButton: msg.dismissButtonLabel || 'Przypomnij później',
					action: msg.moreInfoLink,
				};
				return;
			}

			this.message = this.getImportantAppMessage(!rsvp.user?.boxes['mobileApp']) || this.getImportantPushMessage();
		},

		/**
		 * W przypadku, kiedy nie ma żadnych innych komunikatów z serwera,
		 * sprawdza, czy może zwrócić komunikat dotyczący pobrania aplikacji mobilnej.
		 */
		getImportantAppMessage(hasApp:boolean) : ServiceMessage | undefined {
			const { customerViewAppMessageIgnored } = window.localStorage;
			const appType = mobileApp.canShowDownloadAppPrompt();

			if (customerViewAppMessageIgnored || !appType) { return undefined; }

			if (hasApp) {
				window.localStorage.customerViewAppMessageIgnored = '';
				return undefined;
			}

			return {
				id: 'app',
				type: 'info',
				title: 'Pobierz darmową aplikację Oferteo!',
				message: 'Odbieraj wiadomości od Zlecających w aplikacji',
				button: 'Pobierz',
				dismissButton: 'Przypomnij później',
				action: 'AppDownload',
			};
		},

		/**
		 * W przypadku, kiedy nie ma żadnych ważniejszych komunikatów,
		 * zwraca informację o możliwości włączenia powiadomień Push,
		 * o ile są spełnione stosowne warunki.
		 */
		getImportantPushMessage() : ServiceMessage | undefined {
			try {
				// Warunki do spełnienia:
				// - powiadomienia są obsługiwane przez przeglądarkę
				// - powiadomienia nie są wyłączone lub włączone
				// - komunikat nie został wyłączony więcej niż 3 razy
				// - komunikat nie został wyłączony podczas trwania tej sesji

				const appStore = useAppStore();
				const isSafari = navigator.userAgent.indexOf('Safari') !== -1 && navigator.userAgent.indexOf('Chrome') === -1;
				const pushSupported = typeof Notification === 'function' && Notification.permission === 'default' && !isSafari;
				const { pushPromptDisabledCount, pushPromptDisabledSessionId } = window.localStorage;
				const { sessionId } = appStore;
				const pushPromptDisabled = Number(pushPromptDisabledCount || 0) > 3 || pushPromptDisabledSessionId === sessionId;

				if (pushSupported && !pushPromptDisabled) {
					return {
						id: 'push',
						type: 'info',
						title: '',
						message: 'Włącz powiadomienia push o nowych zapytaniach i nie przegap żadnej okazji na zdobycie Klienta',
						button: 'Włącz',
						dismissButton: 'Przypomnij później',
						action: 'EnablePushNotifications',
					};
				}
			} catch (err) {
				console.warn('getImportantPushMessage', err);
			}

			return undefined;
		},

		/**
		 * Wyświetla informację analitycznę o zobaczeniu komunikatu.
		 */
		messageSeen() {
			if (!this.message) { return; }
			const { id } = this.message;
			const idMsg = Number.isInteger(id) ? id : -1;
			serverFetch(`/profile/important-message-seen.ofer?idMsg=${idMsg}&id=${idMsg}&idTrack=${id}`);
		},

		/**
		 * Ukrywa komunikat i wysyła informacje analityczne do serwera.
		 * @param {*} isSuccess Czy kliknięty został przycisk akcji?
		 */
		async hideMessage(isSuccess = true) {
			if (!this.message) { return; }

			const { id } = this.message;
			const idMsg = Number.isInteger(id) ? id : -1;
			this.message = undefined;
			await serverFetch(`/profile/important-message-${isSuccess ? 'proceed' : 'discard'}.ofer?idMsg=${idMsg}&id=${idMsg}&idTrack=${id}`);
		},
	},
});
